import { format } from 'date-fns';

export const DEBUG_DUMP_KEY = '_debug_dump';

export const isDebugDumpActive =
  typeof window !== 'undefined'
    ? window.localStorage.getItem(DEBUG_DUMP_KEY) === '1'
    : false;

const debugLoggers = new Map<string, DebugLogger>();

// Color constants for logger output and widget
const DEBUG_LOGGER_COLORS = {
  key: '#0057b8',
  date: '#008000',
  msg: '#fff',
  link: '#177cff',
  linkHover: '#4a9aff',
  widgetBg: '#111',
  widgetText: '#fff',
};

// Array of 10 distinct colors for logger keys (high contrast)
const LOGGER_KEY_COLORS = [
  '#d32f2f',
  '#c2185b',
  '#512da8',
  '#1976d2',
  '#00796b',
  '#388e3c',
  '#fbc02d',
  '#f57c00',
];

// Debug Logger uses style objects for CSP Compliance,
// so they are inlined into style="..." attribute directly.
const triggerStyles = {
  position: 'fixed',
  bottom: '0',
  right: '0',
  width: '30px',
  height: '30px',
  zIndex: '2147483646',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px',
  cursor: 'pointer',
};

const widgetStyles = {
  position: 'fixed',
  bottom: '0',
  right: '0',
  zIndex: '2147483647',
  background: 'rgba(30,30,30,0.95)',
  color: '#fff',
  fontFamily: 'sans-serif',
  fontSize: '13px',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  padding: '12px 16px',
  minWidth: '220px',
  maxWidth: '320px',
  opacity: '0',
  transition: 'opacity 0.1s',
  pointerEvents: 'none',
};

const titleStyles = {
  fontWeight: 'bold',
  marginBottom: '8px',
};

const listRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px',
};

const loggerNameStyles = {
  flex: '1',
  cursor: 'pointer',
};

const loggerLinkStyles = {
  color: DEBUG_LOGGER_COLORS.link,
  textDecoration: 'underline',
  cursor: 'pointer',
};

const loggerLinkHoverStyles = {
  color: DEBUG_LOGGER_COLORS.linkHover,
};

/**
 * Converts a style object to an inline style string.
 * @param styles The style object.
 * @returns A string suitable for the 'style' attribute.
 */
function toStyleString(styles: { [key: string]: string | number }): string {
  return Object.entries(styles)
    .map(([key, value]) => {
      // Convert camelCase to kebab-case (e.g., fontSize -> font-size)
      const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${kebabKey}:${value}`;
    })
    .join(';');
}

// Function to choose a color for a logger key
function getLoggerKeyColor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) % LOGGER_KEY_COLORS.length;
  }
  return LOGGER_KEY_COLORS[Math.abs(hash) % LOGGER_KEY_COLORS.length];
}

export class DebugLogger {
  #debugEnabled: boolean;
  #loggerKey: string;
  #collectedLogs: Array<{
    timestamp: number; // store as number for Date.now()
    parts: unknown[];
  }> = [];

  /**
   * Use DebugLogger.getInstance(loggerKey) instead of constructor.
   */
  private constructor(loggerKey: string) {
    this.#loggerKey = loggerKey;
    const storageKey = `_debug_${this.#loggerKey}`;
    this.#debugEnabled =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(storageKey) === '1';

    if (typeof window !== 'undefined') {
      DebugLogger.#maybeInjectWidget();
    }
  }

  /**
   * Returns the singleton DebugLogger for the given loggerKey.
   */
  static getInstance(loggerKey: string): DebugLogger {
    let logger = debugLoggers.get(loggerKey);
    if (!logger) {
      logger = new DebugLogger(loggerKey);
      debugLoggers.set(loggerKey, logger);

      if (DebugLogger.#widgetInjected) {
        DebugLogger.#renderWidgetList();
      }
    }
    return logger;
  }

  debug(...messageParts: unknown[]): void {
    if (this.#debugEnabled) {
      const timestamp = Date.now();
      this.#collectedLogs.push({ timestamp, parts: messageParts });
      const keyColor = getLoggerKeyColor(this.#loggerKey);
      const keyStyle = `color:${keyColor};font-weight:bold`;
      const dateStyle = `color:${DEBUG_LOGGER_COLORS.date}`;
      // biome-ignore lint/suspicious/noConsole: intentional, colorized output for debugging
      console.log(
        `%c${format(timestamp, 'HH:mm:ss')} %c${this.#loggerKey}`,
        dateStyle,
        keyStyle,
        ...messageParts,
      );
    }
  }

  static #widgetInjected = false;

  static #maybeInjectWidget() {
    if (
      DebugLogger.#widgetInjected ||
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !isDebugDumpActive
    ) {
      return;
    }
    DebugLogger.#widgetInjected = true;

    window.addEventListener('DOMContentLoaded', () => {
      if (document.getElementById('debug-widget')) {
        return;
      }

      const trigger = document.createElement('div');
      trigger.id = 'debug-widget-trigger';
      trigger.textContent = '🐞';
      trigger.style.cssText = toStyleString(triggerStyles);
      document.body.appendChild(trigger);

      const widget = document.createElement('div');
      widget.id = 'debug-widget';
      widget.style.cssText = toStyleString(widgetStyles);

      const title = document.createElement('div');
      title.textContent = 'Debug Loggers';
      title.style.cssText = toStyleString(titleStyles);
      widget.appendChild(title);

      const listContainer = document.createElement('div');
      listContainer.id = 'debug-list';
      widget.appendChild(listContainer);

      document.body.appendChild(widget);

      // Since it's not possible to use stylesheets here because of CSP compliance,
      // we replicate :hover behavior with JavaScript.
      let hideTimeout: number;

      const showWidget = () => {
        clearTimeout(hideTimeout);
        widget.style.opacity = '1';
        widget.style.pointerEvents = 'auto';
      };

      const hideWidget = () => {
        hideTimeout = window.setTimeout(() => {
          widget.style.opacity = '0';
          widget.style.pointerEvents = 'none';
        }, 200); // Small delay to allow moving mouse from trigger to widget
      };

      trigger.addEventListener('mouseenter', showWidget);
      widget.addEventListener('mouseenter', showWidget);
      trigger.addEventListener('mouseleave', hideWidget);
      widget.addEventListener('mouseleave', hideWidget);

      DebugLogger.#renderWidgetList();
    });
  }

  static #renderWidgetList() {
    const list = document.getElementById('debug-list');
    if (!list) {
      return;
    }

    const rows: Node[] = [];

    debugLoggers.forEach((logger: DebugLogger, key: string) => {
      const row = document.createElement('div');
      row.style.cssText = toStyleString(listRowStyles);

      const toggleId = `debug-toggle-${key}`;

      const toggle = document.createElement('input');
      toggle.type = 'checkbox';
      toggle.checked = logger.#debugEnabled;
      toggle.id = toggleId;
      toggle.setAttribute('aria-label', `Enable debug for ${key}`);

      const name = document.createElement('label');
      name.htmlFor = toggleId;
      name.textContent = key;
      name.style.cssText = toStyleString(loggerNameStyles);

      const link = document.createElement('a');
      link.href = '#';
      link.textContent = 'Open';
      link.style.cssText = toStyleString(loggerLinkStyles);

      link.addEventListener('mouseenter', () => {
        link.style.cssText = toStyleString({
          ...loggerLinkStyles,
          ...loggerLinkHoverStyles,
        });
      });

      link.addEventListener('mouseleave', () => {
        link.style.cssText = toStyleString(loggerLinkStyles);
      });

      link.addEventListener('click', (event) => {
        event.preventDefault();
        // NOTE: For this to work, your Content-Security-Policy must allow `blob:`
        // in a suitable directive, e.g., `object-src blob:` or `frame-src blob:`.
        const htmlLines = logger.#collectedLogs.map((entry) => {
          const serializedParts = entry.parts.map((part) => {
            const partString =
              typeof part === 'string'
                ? part
                : (JSON.stringify(part, null, 2) ?? String(part));
            return `<span>${partString.replace(/</g, '&lt;')}</span>`;
          });
          const keyColor = getLoggerKeyColor(logger.#loggerKey);
          return `<span style="color:${DEBUG_LOGGER_COLORS.date}">${format(entry.timestamp, 'yyyy-MM-dd HH:mm:ss')}</span> <span style="color:${keyColor};font-weight:bold">${logger.#loggerKey}</span>: ${serializedParts.join(' ')}`;
        });
        const fullHtml = `<!doctype html>\n<html lang="en">\n<head>\n  <meta charset="utf-8">\n  <title>${logger.#loggerKey} – Collected Logs</title>\n</head>\n<body style="font-family:monospace;white-space:pre-wrap">\n${htmlLines.join('<br>')}\n</body>\n</html>`;
        const blob = new Blob([fullHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
      });

      toggle.addEventListener('change', () => {
        logger.#debugEnabled = toggle.checked;
        window.localStorage.setItem(
          `_debug_${key}`,
          toggle.checked ? '1' : '0',
        );
      });

      row.appendChild(toggle);
      row.appendChild(name);
      row.appendChild(link);

      rows.push(row);
    });

    list.replaceChildren(...rows);
  }
}
